import { HStack } from '@pets-ui/HStack/HStack'
import { Lightning } from '@pets-ui/Icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { formatPrice } from '@utils'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CartSalePrice } from '../CartSalePrice/CartSalePrice'
import cartUiI18nInstance from '../i18n/i18nConfig'

const styles = stylex.create({
  discountedPrice: {
    backgroundImage: 'linear-gradient(90deg, #FFF9BA 0%, #FFFBEC 100%)',
  },
})

export type AddonDiscountPriceVariant = 'horizontal' | 'vertical'

export interface AddonDiscountPriceProps {
  /** 幣別 */
  currency?: string
  /** 售價（單價） */
  salePrice?: number
  /** 原價（單價，用於顯示劃線價） */
  originalPrice?: number
  /** 數量 */
  quantity?: number
  /**
   * 排版方式
   * - horizontal: 水平排列（價格和現省標籤左右並排）
   * - vertical: 垂直排列（價格在上，現省標籤在下）
   */
  variant?: AddonDiscountPriceVariant
  /** 容器最小寬度 */
  minWidth?: number
  /** 對齊方式 */
  justifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between'
}

/**
 * 加價購優惠價格元件
 *
 * 用於顯示加價購商品的優惠價、原價劃線以及「現省」標籤
 */
export const AddonDiscountPrice = (props: AddonDiscountPriceProps) => {
  const {
    currency,
    salePrice,
    originalPrice,
    quantity = 1,
    variant = 'vertical',
    minWidth,
    justifyContent = 'flex-end',
  } = props

  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })

  const totalSalePrice = (salePrice ?? 0) * quantity
  const totalOriginalPrice = (originalPrice ?? 0) * quantity
  const discountAmount = ((originalPrice ?? 0) - (salePrice ?? 0)) * quantity

  const priceSection = useMemo(() => (
    <CartSalePrice
      currency={currency}
      salePrice={totalSalePrice}
      quantity={quantity}
      originalPrice={totalOriginalPrice}
      priceType="productPageAddonSalePrice"
    />
  ), [currency, quantity, totalOriginalPrice, totalSalePrice])

  const discountTag = useMemo(() => (
    <HStack alignItems="center" rootStyle={styles.discountedPrice} gap="1px">
      <Lightning fontSize={10} color={colors.red600} />
      <Text fontWeight={600} variant="tiny" color={colors.red600}>
        {t('cartUi.savedAmount', { amount: formatPrice(currency, discountAmount), defaultValue: '現省' })}
      </Text>
    </HStack>
  ), [currency, discountAmount, t])

  if (variant === 'horizontal') {
    return (
      <HStack gap={1} alignItems="flex-end">
        {priceSection}
        {discountTag}
      </HStack>
    )
  }

  return (
    <HStack minWidth={minWidth} justifyContent={justifyContent}>
      <VStack gap={1}>
        {priceSection}
        {discountTag}
      </VStack>
    </HStack>
  )
}
